import { useDisclosure } from "@mantine/hooks";
import { IconPercentage } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { QuoteRequestDto } from "#modules/quoteRequests/infra/fetchQuoteRequest.api";
import { QuoteRequestProcessStepsDto } from "#modules/quoteRequests/infra/fetchQuoteRequestProcessSteps.api";
import { BottomPriceDisplay } from "#modules/quoteRequests/view/BottomPriceDisplay";
import { useQuoteRequestsQuotes } from "#modules/quoteRequests/view/useQuoteRequestsQuotes";
import { useUpdateQuoteRequestInfos } from "#modules/quoteRequests/view/useUpdateQuoteRequestInfos";
import { Icon } from "#shared/icons/Icon";
import { useAnalytics, AnalyticsEvent } from "#shared/useAnalytics";
import { ActionIcon } from "#shared/view/components/ActionIcon";
import { Anchor } from "#shared/view/components/Anchor";
import { Flex } from "#shared/view/components/Flex";
import { Menu } from "#shared/view/components/Menu";
import { Stack } from "#shared/view/components/Stack";
import { NumberInput } from "#shared/view/designSystem/inputs/NumberInput";
import { QuoteRequestLineDto } from "shared";

import { GenerateQuoteInfoModal } from "./GenerateQuoteInfoModal";

type QuoteRequestFooterProps = {
  quoteRequest?: QuoteRequestDto | null;
  isLoading: boolean;
  quoteRequestLines: QuoteRequestLineDto[];
  quoteRequestProcessSteps: QuoteRequestProcessStepsDto;
};

export const QuoteRequestFooter = ({
  quoteRequest,
  isLoading,
  quoteRequestProcessSteps,
  quoteRequestLines,
}: QuoteRequestFooterProps) => {
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const [salesMarginRatio, setSalesMarginRatio] = useState(quoteRequest?.salesMarginRatio || 0);
  const [discountRatio, setDiscountRatio] = useState(quoteRequest?.discountRatio || 0);
  const [pdfModalOpened, { open: openGenerateQuoteInfoModal, close: closeGenerateQuoteInfoModal }] =
    useDisclosure();

  const { quotes } = useQuoteRequestsQuotes(quoteRequest?.id);

  useEffect(() => {
    setSalesMarginRatio(quoteRequest?.salesMarginRatio || 0);
  }, [quoteRequest?.salesMarginRatio]);

  useEffect(() => {
    setDiscountRatio(quoteRequest?.discountRatio || 0);
  }, [quoteRequest?.discountRatio]);

  const { updateQuoteRequestInfos } = useUpdateQuoteRequestInfos({
    shouldShowSuccessToast: false,
  });

  const updateSalesMarginRatio = (newSalesMarginRatio: number) => {
    if (!quoteRequest) {
      setSalesMarginRatio(0);
      return;
    }

    updateQuoteRequestInfos({
      id: quoteRequest.id,
      salesMarginRatio: newSalesMarginRatio,
    });
  };

  const updateDiscountRatio = (newDiscountRatio: number) => {
    if (!quoteRequest) {
      setDiscountRatio(0);
      return;
    }

    updateQuoteRequestInfos({
      id: quoteRequest.id,
      discountRatio: newDiscountRatio,
    });
  };

  return (
    <Flex
      pos="sticky"
      bottom={0}
      w="100%"
      justify="flex-end"
      bg="var(--mantine-color-white)"
      px="xs"
      style={{
        zIndex: 100,
        boxShadow: "0 -4px 8px -4px var(--mantine-color-grey-4)",
      }}
    >
      <Flex gap="xs" pt="xxs" pb="xxs" pl="xxs" align="center">
        <NumberInput
          label={t("quoteRequestLine.generateQuoteModal.labels.salesMarginRatio")}
          leftSection={<IconPercentage size={20} />}
          value={salesMarginRatio}
          onChange={(value) => setSalesMarginRatio(value)}
          min={0}
          decimalScale={2}
          onBlur={(e) => updateSalesMarginRatio(Number(e.target.value))}
        />
        <NumberInput
          label={t("quoteRequestLine.generateQuoteModal.labels.discountRatio")}
          leftSection={<IconPercentage size={20} />}
          value={discountRatio}
          onChange={(value) => setDiscountRatio(value)}
          min={0}
          decimalScale={2}
          onBlur={(e) => updateDiscountRatio(Number(e.target.value))}
        />
        <BottomPriceDisplay
          quoteRequest={quoteRequest}
          quoteRequestLines={quoteRequestLines}
          quoteRequestProcessSteps={quoteRequestProcessSteps}
          isLoading={isLoading}
          salesMarginRatio={salesMarginRatio}
          discountRatio={discountRatio}
        />
        <Stack gap="xxs">
          <GenerateQuoteInfoModal opened={pdfModalOpened} onClose={closeGenerateQuoteInfoModal} />

          {quotes.length === 0 ? (
            <ActionIcon size="md" color="violet.5" onClick={openGenerateQuoteInfoModal}>
              <Icon name="pdf" size={20} />
            </ActionIcon>
          ) : (
            <Menu shadow="md" width={"max-content"}>
              <Menu.Target>
                <ActionIcon size="md" color="violet.5">
                  <Icon name="pdf" size={20} />
                </ActionIcon>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Label>
                  {t("quoteRequestLine.actions.availableQuotes", "Devis à télécharger")}
                </Menu.Label>
                {quotes.map((quote) => (
                  <Menu.Item key={quote.id} leftSection={<Icon name="download" size={14} />}>
                    <Anchor
                      href={quote.pdfFileUrl}
                      style={{ textDecoration: "none" }}
                      component="a"
                      c="inherit"
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      onClick={() =>
                        analytics.capture(AnalyticsEvent.QuotePdfDownloaded, {
                          quote_id: quote.id,
                          version: quote.version,
                          quote_request_id: quoteRequest?.id,
                          area: "footer",
                        })
                      }
                    >
                      {quote.version}
                    </Anchor>
                  </Menu.Item>
                ))}
                <Menu.Divider />
                <Menu.Item
                  leftSection={<Icon name="plus" size={14} />}
                  onClick={openGenerateQuoteInfoModal}
                >
                  {t("quoteRequestLine.actions.newQuote")}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )}
        </Stack>
      </Flex>
    </Flex>
  );
};
